// src/pages/dashboard/AdminOrders.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Receipt, Search, Loader2, X, Truck, CreditCard, CheckCircle2,
  Clock, XCircle, Package, ArrowUpDown, TrendingUp, DollarSign, AlertCircle, RefreshCw,
} from 'lucide-react'
import api from '../../lib/axios'
import { cn } from '../../lib/utils'

const fmt = (n, currency = 'MAD') => {
  const sym = currency === 'MAD' ? 'DH' : currency === 'EUR' ? '€' : currency
  return Number(n || 0).toFixed(2) + ' ' + sym
}

/** Accepte une commande "à plat" (colonnes SQL) OU déjà imbriquée. */
const normalizeOrder = (o) => ({
  ...o,
  customer: o.customer ?? { name: o.customer_name, email: o.customer_email, phone: o.customer_phone },
  product: o.product ?? { id: o.product_id, name: o.product_name },
  tracking: o.tracking ?? { number: o.tracking_number, carrier: o.carrier },
})

const STATUS_META = {
  pending:    { label: 'En attente',     color: 'bg-amber-50 text-amber-700 border-amber-200',      icon: Clock },
  confirmed:  { label: 'Confirmée',      color: 'bg-sky-50 text-sky-700 border-sky-200',            icon: CheckCircle2 },
  processing: { label: 'En préparation', color: 'bg-indigo-50 text-indigo-700 border-indigo-200',   icon: Package },
  shipped:    { label: 'Expédiée',       color: 'bg-blue-50 text-blue-700 border-blue-200',         icon: Truck },
  delivered:  { label: 'Livrée',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200',icon: CheckCircle2 },
  cancelled:  { label: 'Annulée',        color: 'bg-red-50 text-red-700 border-red-200',            icon: XCircle },
}

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending
  const Icon = meta.icon
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border', meta.color)}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  )
}

function AdminOrderModal({ order, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: order.status,
    payment_status: order.payment_status,
    tracking_number: order.tracking?.number || '',
    carrier: order.tracking?.carrier || '',
    admin_note: order.admin_note || '',
  })
  const [saving, setSaving] = useState(false)

  const errText = (e) => {
    const d = e?.response?.data
    return d?.message || (d?.errors ? Object.values(d.errors).flat().join(' • ') : '') || 'Erreur'
  }

  const save = async () => {
    setSaving(true)
    try {
      await api.put(`/admin/orders/${order.id}`, form)
      onSaved(); onClose()
    } catch (e) {
      alert(errText(e))
    } finally { setSaving(false) }
  }

  const quickAction = async (endpoint) => {
    if (!window.confirm('Confirmer ?')) return
    setSaving(true)
    try {
      await api.post(`/admin/orders/${order.id}/${endpoint}`)
      onSaved(); onClose()
    } catch (e) {
      alert(errText(e))
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-[#000a1e]/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl p-7 sm:p-8">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>

        <h3 className="font-display font-bold text-xl text-[#000a1e] mb-1">Commande {order.reference}</h3>
        <p className="text-xs text-slate-500 mb-5">
          {order.customer?.name} · {order.customer?.email}{order.customer?.phone ? ` · ${order.customer.phone}` : ''}
        </p>

        <div className="flex items-center gap-4 p-4 bg-[#eff2ff] rounded-2xl mb-5">
          <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center">
            <Package className="w-6 h-6 text-[#006b5f]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#000a1e] truncate">{order.product?.name}</p>
            <p className="text-xs text-slate-500">{order.quantity} × {fmt(order.unit_price, order.currency)}</p>
          </div>
          <p className="font-display font-bold text-lg text-[#000a1e]">{fmt(order.total, order.currency)}</p>
        </div>

        {order.notes && (
          <p className="text-xs text-slate-600 bg-slate-50 rounded-xl p-3 mb-4"><b>Note client :</b> {order.notes}</p>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-[#000a1e]">Statut</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm">
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#000a1e]">Paiement</span>
              <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm">
                <option value="unpaid">Non payée</option>
                <option value="paid">Payée</option>
                <option value="refunded">Remboursée</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
              placeholder="N° suivi" className="px-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm" />
            <input value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })}
              placeholder="Transporteur" className="px-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm" />
          </div>

          <textarea value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })}
            rows={3} placeholder="Note admin" className="w-full px-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm resize-none" />
        </div>

        <div className="mt-6 pt-5 border-t border-slate-100">
          <p className="text-[10px] font-bold uppercase text-slate-400 mb-3">Actions rapides</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => quickAction('mark-paid')} disabled={saving}
              className="px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60">
              <CreditCard className="w-3.5 h-3.5" /> Payée
            </button>
            <button onClick={() => quickAction('mark-shipped')} disabled={saving}
              className="px-3 py-2 rounded-xl bg-blue-50 text-blue-700 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60">
              <Truck className="w-3.5 h-3.5" /> Expédiée
            </button>
            <button onClick={() => quickAction('mark-delivered')} disabled={saving}
              className="px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60">
              <CheckCircle2 className="w-3.5 h-3.5" /> Livrée
            </button>
            <button onClick={() => quickAction('cancel')} disabled={saving}
              className="px-3 py-2 rounded-xl bg-red-50 text-red-700 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60">
              <XCircle className="w-3.5 h-3.5" /> Annuler
            </button>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 font-semibold text-sm">Fermer</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-3 rounded-xl bg-[#006b5f] text-white font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  )
}

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'pending', label: 'En attente' },
  { id: 'confirmed', label: 'Confirmées' },
  { id: 'processing', label: 'En préparation' },
  { id: 'shipped', label: 'Expédiées' },
  { id: 'delivered', label: 'Livrées' },
  { id: 'cancelled', label: 'Annulées' },
]

export default function AdminOrders() {
  const [orders, setOrders] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [sortDesc, setSortDesc] = useState(true)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = { per_page: 50 }
      if (filter !== 'all') params.status = filter
      if (search && search.trim()) params.q = search.trim()

      // allSettled : une erreur sur /stats ne doit JAMAIS cacher la liste
      const [ordersRes, statsRes] = await Promise.allSettled([
        api.get('/admin/orders', { params }),
        api.get('/admin/orders/stats'),
      ])

      if (ordersRes.status === 'rejected') throw ordersRes.reason

      const body = ordersRes.value.data
      const rows = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : body?.data?.data ?? []
      setOrders(rows.map(normalizeOrder))

      setStats(statsRes.status === 'fulfilled' ? statsRes.value.data?.data ?? null : null)
    } catch (e) {
      console.error('❌ LOAD ERROR:', e?.response?.status, e?.response?.data || e)
      const d = e?.response?.data
      setError(
        d?.message
          ? `${e.response.status} — ${d.message}${d.your_role !== undefined ? ` (rôle détecté : ${d.your_role ?? 'aucun'})` : ''}`
          : 'Impossible de charger les commandes.'
      )
      setOrders([])
    } finally { setLoading(false) }
  }, [filter, search])

  useEffect(() => {
    const t = setTimeout(load, 200)
    return () => clearTimeout(t)
  }, [load])

  const sorted = useMemo(() => {
    const arr = [...orders]
    arr.sort((a, b) => {
      const da = new Date(a.created_at).getTime() || 0
      const db = new Date(b.created_at).getTime() || 0
      return sortDesc ? db - da : da - db
    })
    return arr
  }, [orders, sortDesc])

  return (
    <div className="min-h-screen bg-[#f9f9ff]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-bold text-3xl text-[#000a1e] flex items-center gap-2">
              <Receipt className="w-7 h-7 text-[#006b5f]" /> Commandes
            </h1>
            <p className="text-sm text-slate-500 mt-1">Gérez toutes les commandes.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm w-full sm:w-72" />
            </div>
            <button onClick={() => setSortDesc(v => !v)} className="p-2.5 rounded-xl border border-slate-200 bg-white">
              <ArrowUpDown className="w-4 h-4 text-slate-500" />
            </button>
            <button onClick={load} className="p-2.5 rounded-xl border border-slate-200 bg-white" title="Recharger">
              <RefreshCw className={cn('w-4 h-4 text-slate-500', loading && 'animate-spin')} />
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Total', value: stats.total_orders, color: 'text-[#000a1e]', icon: Package },
              { label: 'CA payé', value: fmt(stats.total_revenue), color: 'text-emerald-600', icon: DollarSign },
              { label: "Aujourd'hui", value: stats.today, color: 'text-[#006b5f]', icon: TrendingUp },
              { label: 'En attente CA', value: fmt(stats.pending_revenue), color: 'text-amber-600', icon: Clock },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
                <p className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
                  <s.icon className="w-3 h-3" /> {s.label}
                </p>
                <p className={cn('font-display font-bold text-2xl mt-1', s.color)}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-100 p-2 flex gap-1 overflow-x-auto mb-6 shadow-sm">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={cn(
                'px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition',
                filter === f.id ? 'bg-[#006b5f] text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'
              )}>
              {f.label}
            </button>
          ))}
        </div>

        {loading && <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" /></div>}

        {error && (
          <div className="max-w-lg mx-auto p-4 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-600 text-center flex items-center gap-2 justify-center">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {!loading && !error && sorted.length === 0 && (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune commande.</p>
          </div>
        )}

        {!loading && sorted.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto shadow-sm">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-[10px] font-bold uppercase text-slate-400">
                  <th className="px-4 py-3">Référence</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3 hidden md:table-cell">Produit</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((o) => (
                  <tr key={o.id} onClick={() => setSelected(o)}
                    className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-bold text-[#006b5f]">{o.reference}</span>
                      {o.group_reference && <p className="text-[10px] text-slate-400">{o.group_reference}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-[#000a1e] truncate max-w-[160px]">{o.customer?.name || '—'}</p>
                      <p className="text-xs text-slate-500 truncate max-w-[160px]">{o.customer?.email || '—'}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-sm text-[#000a1e] truncate max-w-[200px]">{o.product?.name || '—'}</p>
                      <p className="text-xs text-slate-500">× {o.quantity}</p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={o.status} />
                      <p className={cn('text-[10px] mt-1 font-semibold', o.payment_status === 'paid' ? 'text-emerald-600' : 'text-slate-400')}>
                        {o.payment_status === 'paid' ? 'Payée' : o.payment_status === 'refunded' ? 'Remboursée' : 'Non payée'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right font-display font-bold text-sm">{fmt(o.total, o.currency)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString('fr-FR') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <AdminOrderModal order={selected} onClose={() => setSelected(null)} onSaved={load} />}
    </div>
  )
}